/* eslint-disable react/require-default-props */
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import { LoadingButton } from '@mui/lab';
import { CircularProgress, useMediaQuery, useTheme } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ModalHeader from './ModalHeader';

interface Props {
  open: boolean;
  handleClose: () => void;
  title: string;
  message: string;
  negativeAction?: () => void;
  positiveAction: () => void;
  negativeText?: string;
  positiveText: string;
  isLoading?: boolean;
  isSuccess?: boolean;
  isDismissable?: boolean;
  isSmall?: boolean;
  hideCloseButton?: boolean;
  noWrapButtonsText?: boolean; // confirm no overflow when passing this as true
  actionIsLoading?: boolean;
  hideButtons?: boolean;
}

export default function ConfirmationModal({
  open,
  handleClose,
  title,
  message,
  negativeAction,
  positiveAction,
  negativeText,
  positiveText,
  isLoading = false,
  isSuccess = false,
  isDismissable = true,
  isSmall = false,
  hideCloseButton = false,
  noWrapButtonsText = false,
  actionIsLoading = false,
  hideButtons = false,
}: Props) {
  const theme = useTheme();
  const isDesktop = useMediaQuery('(min-width:900px)');
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));

  const style = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: isDesktop ? (isSmall ? 480 : 600) : '90%',
    bgcolor: 'background.paper',
    borderRadius: '5px',
    boxShadow: 24,
    p: 1,
  };

  return (
    <Modal
      open={open}
      onClose={isDismissable ? handleClose : undefined}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description">
      <Box sx={style}>
        <ModalHeader
          name={title}
          handleClose={handleClose}
          isDismissable={isDismissable}
          hideCloseButton={
            hideCloseButton || (isXs && !!negativeText && !!negativeAction)
          }
        />
        <Box
          style={{
            padding: '20px 10px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '20px',
          }}>
          {isSuccess && (
            <CheckCircleIcon
              sx={{
                fontSize: 64,
                color: theme.palette.success.main,
              }}
            />
          )}
          <Typography
            id="modal-modal-description"
            variant="body1"
            whiteSpace="pre-line"
            className="center">
            {message}
          </Typography>
          {isLoading && !isSuccess && <CircularProgress />}
        </Box>
        {!hideButtons && (
          <Box
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: negativeText ? 'space-between' : 'flex-end',
              padding: isSmall || isXs ? '10px' : '20px 10px',
              borderTop: '1px solid #F5F5F5',
              gap: theme.spacing(1),
            }}>
            {negativeText && (
              <Button
                variant="outlined"
                className="secondary"
                onClick={negativeAction}
                sx={{
                  whiteSpace: noWrapButtonsText ? 'nowrap' : 'normal',
                }}>
                {negativeText}
              </Button>
            )}

            <LoadingButton
              className="primary"
              variant="contained"
              color="primary"
              onClick={positiveAction}
              loading={actionIsLoading}
              sx={{
                whiteSpace: noWrapButtonsText ? 'nowrap' : 'normal',
              }}>
              <span>{positiveText}</span>
            </LoadingButton>
          </Box>
        )}
      </Box>
    </Modal>
  );
}
